/**
 * VerificationConsole — Iter 47.4 (2026-02)
 *
 * Tightly-scoped operational console for executing the Iter 47 production
 * verification cycle. Super-admin only. Wraps the existing P0/P1 wizard
 * API surface (no new backend endpoints) with:
 *   • Session token capture (in-memory only, never persisted)
 *   • Linear wizard cards (upload → validate → preview → confirm →
 *     dry-run/real execute → checkpoint download → rollback)
 *   • Phase-aware button gating (matches backend state machine)
 *   • Typed-confirmation modal for every destructive action
 *   • Forensic block surfacing post-execute (checksums, lineage)
 *   • Per-area 8-point verification tracker (localStorage-backed,
 *     exportable as JSON)
 *
 * Out of scope (intentionally, per PRD HOLD): replay tooling, overlay
 * evaluation, multi-currency, scheduled checkpointing, dashboards.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    UploadSimple, Eye, Lightning, ShieldCheck, ArrowUUpLeft,
    DownloadSimple, ArrowsClockwise, Lock, Warning, Key, ListChecks,
    CheckCircle, XCircle, CircleDashed, Copy, FloppyDisk, FileText,
    Stack,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { api, formatApiError, API_BASE, getToken } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import DestructiveConfirmDialog from "../../components/admin/DestructiveConfirmDialog";


// ════════════════════════════════════════════════════════════════════════
// Constants — mirror backend contract (do not edit without a paired BE change)
// ════════════════════════════════════════════════════════════════════════

const CONFIRM_PHRASE = "RESTORE PRODUCTION";

// Iter 47.9 — Build identification marker.
// Bumped per console change so the operator can visually confirm the
// deployed production bundle includes the latest fix. If the BUILD_TAG
// shown in the header pill does NOT match the value below after a deploy,
// the deployment pipeline / CDN is serving a stale bundle.
const BUILD_TAG = "iter-47.9-bundled-guard";

const VERIFICATION_AREAS = [
    { key: "snapshot_integrity",            label: "1. Snapshot integrity" },
    { key: "schema_version_compatibility",  label: "2. Schema-version compatibility" },
    { key: "rollback_correctness",          label: "3. Rollback correctness" },
    { key: "checkpoint_download_usability", label: "4. Checkpoint download usability" },
    { key: "audit_log_completeness",        label: "5. Audit-log completeness" },
    { key: "guard_rejection_behaviour",     label: "6. Guard rejection behaviour" },
    { key: "restore_sequencing_integrity",  label: "7. Restore sequencing integrity" },
    { key: "immutable_billing_zero_corruption", label: "8. Immutable-billing zero corruption" },
];

const LS_RUN_KEY = "reedos_verification_run_v1";

// Phase guards — keep in lockstep with backend state machine
const canValidate = (job) => job && ["created", "validating", "previewed"].includes(job.phase);
const canPreview  = (job) => job && ["previewed", "awaiting_confirm"].includes(job.phase);
const canConfirm  = (job) => job && job.phase === "previewed";
const canExecute  = (job) => job && job.phase === "awaiting_confirm";
const canRollback = (job) => job && ["completed", "failed"].includes(job.phase) && job.checkpoint_id;


// ════════════════════════════════════════════════════════════════════════
// Helpers
// ════════════════════════════════════════════════════════════════════════

const phaseBadgeStyle = (phase) => {
    switch (phase) {
        case "created":          return "bg-slate-100 text-slate-700";
        case "validating":
        case "previewed":        return "bg-blue-100 text-blue-700";
        case "awaiting_confirm": return "bg-amber-100 text-amber-800";
        case "executing":        return "bg-violet-100 text-violet-700";
        case "completed":        return "bg-emerald-100 text-emerald-700";
        case "failed":           return "bg-rose-100 text-rose-700";
        case "rolled_back":      return "bg-fuchsia-100 text-fuchsia-700";
        default:                 return "bg-slate-100 text-slate-700";
    }
};

const todayStamp = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
};

const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const copy = async (val, label) => {
    try {
        await navigator.clipboard.writeText(String(val));
        toast.success(`Copied ${label}`);
    } catch {
        toast.error("Clipboard not available");
    }
};

// Build axios config that injects the destructive-guard token header.
const tokenHeader = (token) =>
    token ? { headers: { "X-Import-Restore-Token": token } } : undefined;


// ════════════════════════════════════════════════════════════════════════
// Verification-run state (localStorage)
// ════════════════════════════════════════════════════════════════════════

const newRunRecord = (env) => ({
    id: `VRUN-LOCAL-${Date.now()}`,
    environment_identifier: env,
    started_at: new Date().toISOString(),
    last_updated_at: new Date().toISOString(),
    operator_notes: "",
    areas: VERIFICATION_AREAS.reduce((acc, a) => {
        acc[a.key] = { status: "pending", note: "", at: null };
        return acc;
    }, {}),
    actions: [],
});

const loadRun = () => {
    try {
        const raw = localStorage.getItem(LS_RUN_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const persistRun = (run) => {
    try {
        localStorage.setItem(LS_RUN_KEY, JSON.stringify(run));
    } catch {
        // localStorage quota / disabled — non-fatal
    }
};


// ════════════════════════════════════════════════════════════════════════
// Page
// ════════════════════════════════════════════════════════════════════════

export default function VerificationConsole() {
    const { user, can } = useAuth();
    const navigate = useNavigate();

    // Iter 70 — permission-based gate (data:verify). Defence in depth
    // — the App.js route-level RequirePerm already short-circuits to
    // a 403 screen for users without the permission, but the in-page
    // effect remains so a stale tab fallback-redirects too.
    useEffect(() => {
        if (user && !can("data:verify")) {
            navigate("/dashboard", { replace: true });
        }
    }, [user, can, navigate]);

    // ────── Session token (in-memory only) ──────
    const [token, setToken] = useState("");

    // ────── Server-side config health (Iter 47.6) ──────
    // Polled on mount + after every navigation back to this page. Boolean
    // payload only — the server never discloses the actual token value.
    const [health, setHealth] = useState({ loading: true });
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/admin/import/health");
                if (!cancelled) setHealth({ loading: false, ...data });
            } catch (err) {
                if (!cancelled) setHealth({
                    loading: false,
                    error: formatApiError(err, "Health probe failed"),
                });
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // ────── Current job + last-step results ──────
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(null);   // step key currently running
    const [preview, setPreview] = useState(null);
    const [executeResult, setExecuteResult] = useState(null);
    const [rollbackResult, setRollbackResult] = useState(null);
    const [dryRunResult, setDryRunResult] = useState(null);

    // ────── Destructive modal control ──────
    const [confirmDialog, setConfirmDialog] = useState(null); // 'confirm' | 'execute' | 'rollback'

    // ────── Verification run (localStorage) ──────
    const [run, setRun] = useState(() => loadRun() || newRunRecord(API_BASE));
    useEffect(() => {
        persistRun(run);
    }, [run]);

    const updateRun = (patch) => {
        setRun((r) => ({ ...r, ...patch, last_updated_at: new Date().toISOString() }));
    };

    const recordAction = (stepName, summary) => {
        setRun((r) => ({
            ...r,
            last_updated_at: new Date().toISOString(),
            actions: [
                ...r.actions,
                {
                    at: new Date().toISOString(),
                    step: stepName,
                    summary,
                },
            ],
        }));
    };

    const setAreaStatus = (areaKey, status, note = "") => {
        setRun((r) => ({
            ...r,
            last_updated_at: new Date().toISOString(),
            areas: {
                ...r.areas,
                [areaKey]: { status, note, at: new Date().toISOString() },
            },
        }));
    };

    // ════════════════════════════════════════════════════════════════════
    // Wizard step handlers
    // ════════════════════════════════════════════════════════════════════

    const startBundled = async () => {
        // Iter 47.8 — clear prior job + downstream state IMMEDIATELY on
        // click so a stale upload-job can never remain visible while the
        // bundled call is in flight or after a silent failure. Backend
        // probe confirmed /upload-bundled returns correct payload; this
        // change exclusively addresses frontend display lineage.
        setJob(null);
        setPreview(null);
        setExecuteResult(null);
        setRollbackResult(null);
        setDryRunResult(null);
        setLoading("upload");
        try {
            const { data } = await api.post("/admin/import/upload-bundled");
            // Iter 47.9 — Defensive response-shape assertion. If a stale
            // bundle / proxy ever served a wrong-shape payload, surface
            // it LOUDLY instead of silently rendering mismatched state.
            if (!data?.job || data.job.source !== "bundled") {
                console.error("[VerificationConsole] /upload-bundled returned unexpected shape", data);
                toast.error(
                    `Unexpected response from /upload-bundled (source=${data?.job?.source ?? "missing"}). Bundle may be stale — hard-reload and verify build tag.`
                );
                return;
            }
            setJob(data.job);
            recordAction("upload-bundled", {
                job_id: data.job.id,
                source: "bundled",
                checksum: data.job.checksum,
                total_documents: data.summary?.total_documents,
                schema_version: data.summary?.schema_version,
            });
            toast.success(`Job ${data.job.id} created from bundled snapshot`);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to create bundled job"));
        } finally {
            setLoading(null);
        }
    };

    const startUpload = async (file) => {
        if (!file) return;
        // Iter 47.8 — same immediate-clear pattern as startBundled.
        setJob(null);
        setPreview(null);
        setExecuteResult(null);
        setRollbackResult(null);
        setDryRunResult(null);
        setLoading("upload");
        try {
            const form = new FormData();
            form.append("file", file);
            const resp = await fetch(`${API_BASE}/admin/import/upload`, {
                method: "POST",
                headers: { Authorization: `Bearer ${getToken()}` },
                body: form,
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data?.detail || "Upload failed");
            // Iter 47.9 — Defensive response-shape assertion (mirror of bundled).
            if (!data?.job || data.job.source !== "upload") {
                console.error("[VerificationConsole] /upload returned unexpected shape", data);
                toast.error(
                    `Unexpected response from /upload (source=${data?.job?.source ?? "missing"}). Bundle may be stale — hard-reload and verify build tag.`
                );
                return;
            }
            setJob(data.job);
            recordAction("upload", {
                job_id: data.job.id,
                source: "upload",
                filename: file.name,
                checksum: data.job.checksum,
                total_documents: data.summary?.total_documents,
            });
            toast.success(`Job ${data.job.id} created from uploaded file`);
        } catch (err) {
            toast.error(err?.message || "Upload failed");
        } finally {
            setLoading(null);
        }
    };

    const runValidate = async () => {
        if (!job) return;
        setLoading("validate");
        try {
            const { data } = await api.post(`/admin/import/${job.id}/validate`);
            setJob(data.job);
            recordAction("validate", {
                job_id: job.id,
                errors: data.validation_report?.structural?.errors?.length || 0,
                warnings: data.validation_report?.structural?.warnings?.length || 0,
                incoming_counts: data.validation_report?.incoming_counts,
                current_db_counts: data.validation_report?.current_db_counts,
            });
            toast.success("Validation complete");
        } catch (err) {
            toast.error(formatApiError(err, "Validation failed"));
        } finally {
            setLoading(null);
        }
    };

    const runPreview = async () => {
        if (!job) return;
        setLoading("preview");
        try {
            const { data } = await api.get(`/admin/import/${job.id}/preview`);
            setPreview(data);
            recordAction("preview", {
                job_id: job.id,
                sample_collections: Object.keys(data.sample_records || {}),
                import_order: data.import_order,
            });
            toast.success("Preview loaded");
        } catch (err) {
            toast.error(formatApiError(err, "Preview failed"));
        } finally {
            setLoading(null);
        }
    };

    const runConfirm = async (dryRun) => {
        if (!job) return;
        if (!token) {
            toast.error("Enter the IMPORT_RESTORE_TOKEN before continuing");
            return;
        }
        setLoading("confirm");
        try {
            const { data } = await api.post(
                `/admin/import/${job.id}/confirm`,
                {
                    mode: "STRICT_RESTORE",
                    confirmation_phrase: CONFIRM_PHRASE,
                    dry_run: dryRun,
                },
                tokenHeader(token),
            );
            setJob(data.job);
            setConfirmDialog(null);
            recordAction("confirm", {
                job_id: job.id,
                dry_run: dryRun,
                phase_after: data.job.phase,
            });
            toast.success(`Confirmed (${dryRun ? "dry-run" : "real"})`);
        } catch (err) {
            toast.error(formatApiError(err, "Confirm rejected"));
        } finally {
            setLoading(null);
        }
    };

    const runExecute = async () => {
        if (!job) return;
        setLoading("execute");
        try {
            const { data } = await api.post(
                `/admin/import/${job.id}/execute`,
                { confirmation_phrase: CONFIRM_PHRASE },
                tokenHeader(token),
            );
            setJob(data.job);
            setConfirmDialog(null);
            if (data.dry_run) {
                setDryRunResult(data.plan);
                recordAction("execute.dry_run", {
                    job_id: job.id,
                    would_restore_total: Object.values(data.plan?.would_restore || {}).reduce((a, b) => a + b, 0),
                });
                toast.success("Dry-run plan returned (no DB mutation)");
            } else {
                setExecuteResult(data);
                recordAction("execute", {
                    job_id: job.id,
                    checkpoint_id: data.checkpoint?.id,
                    checkpoint_checksum: data.checkpoint?.checksum,
                    total_documents_restored: data.execution_log?.total_documents_restored,
                });
                toast.success("Restore executed");
            }
        } catch (err) {
            toast.error(formatApiError(err, "Execute failed"));
        } finally {
            setLoading(null);
        }
    };

    const runRollback = async () => {
        if (!job) return;
        setLoading("rollback");
        try {
            const { data } = await api.post(
                `/admin/import/${job.id}/rollback`,
                { confirmation_phrase: CONFIRM_PHRASE },
                tokenHeader(token),
            );
            setJob(data.job);
            setRollbackResult(data.rollback_log);
            setConfirmDialog(null);
            recordAction("rollback", {
                job_id: job.id,
                total_documents_restored: data.rollback_log?.total_documents_restored,
            });
            toast.success("Rollback complete");
        } catch (err) {
            toast.error(formatApiError(err, "Rollback failed"));
        } finally {
            setLoading(null);
        }
    };

    const downloadCheckpoint = async () => {
        const cid = executeResult?.checkpoint?.id || job?.checkpoint_id;
        if (!cid) {
            toast.error("No checkpoint id available yet");
            return;
        }
        setLoading("checkpoint-download");
        try {
            const { data } = await api.get(`/admin/checkpoints/${cid}/download`);
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            saveBlob(blob, `checkpoint-${cid}-${todayStamp()}.json`);
            recordAction("checkpoint.download", {
                checkpoint_id: cid,
                size_bytes: data.size_bytes,
                checksum: data.checksum,
            });
            toast.success(`Checkpoint ${cid} downloaded`);
        } catch (err) {
            toast.error(formatApiError(err, "Checkpoint download failed"));
        } finally {
            setLoading(null);
        }
    };

    const refreshJob = async () => {
        if (!job) return;
        try {
            const { data } = await api.get(`/admin/import/${job.id}`);
            setJob(data.job);
        } catch (err) {
            toast.error(formatApiError(err, "Refresh failed"));
        }
    };

    // ════════════════════════════════════════════════════════════════════
    // Verification-run export / reset
    // ════════════════════════════════════════════════════════════════════

    const exportRun = () => {
        const record = {
            ...run,
            environment_identifier: API_BASE,
            exported_at: new Date().toISOString(),
            exported_by: user?.email,
            framework_version: "iter_47_p0_p1",
            note: "Local verification-run record. Persist this file as the formal verification artefact until the production_verification_runs collection endpoint is implemented (PRD: Production Verification Protocol).",
        };
        const blob = new Blob([JSON.stringify(record, null, 2)], { type: "application/json" });
        saveBlob(blob, `verification-run-${todayStamp()}.json`);
        toast.success("Verification record exported");
    };

    const resetRun = () => {
        if (!window.confirm("Start a new verification run? The current local record will be cleared. Export it first if you need it.")) return;
        const fresh = newRunRecord(API_BASE);
        setRun(fresh);
        setJob(null);
        setPreview(null);
        setExecuteResult(null);
        setRollbackResult(null);
        setDryRunResult(null);
        toast.success("New verification run started");
    };

    // ════════════════════════════════════════════════════════════════════
    // Render
    // ════════════════════════════════════════════════════════════════════

    const forensics = job?.forensics;
    const checkpointId = executeResult?.checkpoint?.id || job?.checkpoint_id;

    return (
        <div data-testid="verification-console-page">
            <PageHeader
                eyebrow="Iter 47 · Restore & Recovery"
                title="Verification Console"
                description="Operational console for the controlled restore-verification cycle. Super-admin only."
                actions={
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono text-xs" data-testid="env-badge">
                            {API_BASE}
                        </Badge>
                        <Badge
                            variant="outline"
                            className="font-mono text-[10px] bg-slate-50 border-slate-300 text-slate-700"
                            title="Deployed bundle marker — must match the latest committed BUILD_TAG. If you see an older value, the production CDN is serving a stale bundle (hard-reload or redeploy)."
                            data-testid="build-tag-badge"
                        >
                            build: {BUILD_TAG}
                        </Badge>
                        <Button
                            variant="outline"
                            onClick={exportRun}
                            data-testid="export-run-btn"
                        >
                            <FloppyDisk size={16} className="mr-1.5" />
                            Export run record
                        </Button>
                        <Button
                            variant="outline"
                            onClick={resetRun}
                            data-testid="reset-run-btn"
                        >
                            New run
                        </Button>
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-6 grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* ─────────── LEFT — Wizard (2/3 width on xl) ─────────── */}
                <div className="xl:col-span-2 space-y-5">

                    {/* SESSION SETUP */}
                    <StepCard
                        icon={Key}
                        title="Session setup"
                        subtitle="Enter the IMPORT_RESTORE_TOKEN for this environment. Stored in browser memory only — never written to localStorage."
                        testId="step-session-setup"
                    >
                        <HealthStatusPill health={health} />
                        <div className="flex items-center gap-2">
                            <Input
                                type="password"
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                placeholder="IMPORT_RESTORE_TOKEN value"
                                className="font-mono"
                                data-testid="session-token-input"
                            />
                            <Badge variant={token ? "default" : "outline"} data-testid="session-token-state">
                                {token ? `Set (${token.length} chars)` : "Not set"}
                            </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-2 flex items-start gap-1.5">
                            <Lock size={12} className="mt-0.5" />
                            Required for confirm / execute / rollback. Without it, the
                            backend's destructive guard returns 403 (audit-logged).
                        </p>
                    </StepCard>

                    {/* STEP 1 — UPLOAD */}
                    <StepCard
                        icon={UploadSimple}
                        title="Step 1 · Snapshot selection"
                        subtitle="Create a new import job from the bundled snapshot or an uploaded JSON file."
                        testId="step-upload"
                        active={!job}
                    >
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                onClick={startBundled}
                                disabled={loading === "upload"}
                                data-testid="upload-bundled-btn"
                            >
                                {loading === "upload" ? "Creating…" : "Use bundled snapshot"}
                            </Button>
                            <label className="inline-flex items-center">
                                <input
                                    type="file"
                                    accept="application/json,.json"
                                    className="hidden"
                                    onChange={(e) => startUpload(e.target.files?.[0])}
                                    data-testid="upload-file-input"
                                />
                                <span
                                    role="button"
                                    className="inline-flex items-center cursor-pointer h-9 px-4 rounded-md border border-slate-200 bg-white text-sm font-medium hover:bg-slate-50"
                                    data-testid="upload-file-btn"
                                >
                                    Upload JSON file…
                                </span>
                            </label>
                        </div>

                        {job && (
                            <JobSummary
                                job={job}
                                onRefresh={refreshJob}
                                onCopyId={() => copy(job.id, "Job ID")}
                            />
                        )}
                    </StepCard>

                    {/* STEP 2 — VALIDATE */}
                    <StepCard
                        icon={FileText}
                        title="Step 2 · Validate"
                        subtitle="Run structural validation + capture current DB counts for diff."
                        testId="step-validate"
                        active={canValidate(job)}
                        disabledReason={
                            !job ? "Create a job first" :
                            !canValidate(job) ? `Phase = ${job.phase}` : null
                        }
                    >
                        <Button
                            onClick={runValidate}
                            disabled={!canValidate(job) || loading === "validate"}
                            data-testid="validate-btn"
                        >
                            {loading === "validate" ? "Validating…" : "Run validation"}
                        </Button>

                        {job?.validation_report && (
                            <ValidationReport report={job.validation_report} />
                        )}
                    </StepCard>

                    {/* STEP 3 — PREVIEW */}
                    <StepCard
                        icon={Eye}
                        title="Step 3 · Preview"
                        subtitle="Sample records per collection + topological import order."
                        testId="step-preview"
                        active={canPreview(job)}
                        disabledReason={!canPreview(job) ? "Validate first" : null}
                    >
                        <Button
                            onClick={runPreview}
                            disabled={!canPreview(job) || loading === "preview"}
                            variant="outline"
                            data-testid="preview-btn"
                        >
                            {loading === "preview" ? "Loading…" : "Load preview"}
                        </Button>

                        {preview && <PreviewPanel preview={preview} />}
                    </StepCard>

                    {/* STEP 4 — CONFIRM */}
                    <StepCard
                        icon={ShieldCheck}
                        title="Step 4 · Confirm mode"
                        subtitle="Lock in STRICT_RESTORE mode and the dry-run flag. Three-layer guard runs here: token + phrase + state-machine."
                        testId="step-confirm"
                        active={canConfirm(job)}
                        disabledReason={!canConfirm(job) ? "Validate first" : null}
                    >
                        <div className="flex flex-wrap gap-2">
                            <Button
                                onClick={() => setConfirmDialog("confirm-dryrun")}
                                disabled={!canConfirm(job) || !token || loading === "confirm"}
                                variant="outline"
                                data-testid="confirm-dryrun-btn"
                            >
                                Confirm (dry-run = true)
                            </Button>
                            <Button
                                onClick={() => setConfirmDialog("confirm-real")}
                                disabled={!canConfirm(job) || !token || loading === "confirm"}
                                className="bg-rose-600 hover:bg-rose-500 text-white"
                                data-testid="confirm-real-btn"
                            >
                                <Warning size={14} className="mr-1.5" />
                                Confirm (REAL — destructive next)
                            </Button>
                        </div>
                        {!token && canConfirm(job) && (
                            <p className="text-xs text-rose-600 mt-2">
                                Enter the session token above first.
                            </p>
                        )}
                    </StepCard>

                    {/* STEP 5 — EXECUTE */}
                    <StepCard
                        icon={Lightning}
                        title="Step 5 · Execute"
                        subtitle="Checkpoint → wipe → restore. Dry-run executes only if confirm was made with dry_run=true."
                        testId="step-execute"
                        active={canExecute(job)}
                        disabledReason={!canExecute(job) ? "Confirm first" : null}
                    >
                        <Button
                            onClick={() => setConfirmDialog("execute")}
                            disabled={!canExecute(job) || !token || loading === "execute"}
                            className={job?.dry_run ? "" : "bg-rose-600 hover:bg-rose-500 text-white"}
                            data-testid="execute-btn"
                        >
                            {loading === "execute" ? "Executing…" :
                             job?.dry_run ? "Execute dry-run plan" :
                             <><Warning size={14} className="mr-1.5" />Execute restore (DESTRUCTIVE)</>}
                        </Button>

                        {dryRunResult && <DryRunPanel plan={dryRunResult} />}
                        {executeResult && <ExecuteResultPanel result={executeResult} />}
                        {forensics && <ForensicBlock forensics={forensics} onCopy={copy} />}
                    </StepCard>

                    {/* STEP 6 — CHECKPOINT DOWNLOAD */}
                    <StepCard
                        icon={DownloadSimple}
                        title="Step 6 · Checkpoint download"
                        subtitle="Save the pre-execute checkpoint payload locally for off-platform DR storage."
                        testId="step-checkpoint-download"
                        active={!!checkpointId}
                        disabledReason={!checkpointId ? "Execute first" : null}
                    >
                        <Button
                            onClick={downloadCheckpoint}
                            disabled={!checkpointId || loading === "checkpoint-download"}
                            variant="outline"
                            data-testid="checkpoint-download-btn"
                        >
                            <DownloadSimple size={14} className="mr-1.5" />
                            {loading === "checkpoint-download" ? "Downloading…" : `Download checkpoint JSON`}
                        </Button>
                        {checkpointId && (
                            <div className="text-xs text-slate-500 mt-2 font-mono flex items-center gap-2">
                                <span>{checkpointId}</span>
                                <button onClick={() => copy(checkpointId, "Checkpoint ID")} className="text-slate-400 hover:text-slate-900">
                                    <Copy size={12} />
                                </button>
                            </div>
                        )}
                    </StepCard>

                    {/* STEP 7 — ROLLBACK */}
                    <StepCard
                        icon={ArrowUUpLeft}
                        title="Step 7 · Rollback"
                        subtitle="Restore the database from this job's checkpoint. Appends to forensics.rollback_history (append-only)."
                        testId="step-rollback"
                        active={canRollback(job)}
                        disabledReason={!canRollback(job) ? "Execute first" : null}
                    >
                        <Button
                            onClick={() => setConfirmDialog("rollback")}
                            disabled={!canRollback(job) || !token || loading === "rollback"}
                            className="bg-rose-600 hover:bg-rose-500 text-white"
                            data-testid="rollback-btn"
                        >
                            <Warning size={14} className="mr-1.5" />
                            {loading === "rollback" ? "Rolling back…" : "Rollback restore"}
                        </Button>

                        {rollbackResult && <RollbackPanel result={rollbackResult} />}
                        {forensics?.rollback_history?.length > 0 && (
                            <RollbackHistory history={forensics.rollback_history} />
                        )}
                    </StepCard>
                </div>

                {/* ─────────── RIGHT — Run Tracker ─────────── */}
                <aside className="xl:col-span-1">
                    <div className="sticky top-24 space-y-4" data-testid="run-tracker-panel">
                        <RunTracker
                            run={run}
                            onAreaChange={setAreaStatus}
                            onNotesChange={(notes) => updateRun({ operator_notes: notes })}
                        />
                    </div>
                </aside>
            </div>

            {/* ─────────── Destructive Confirm Modals ─────────── */}
            <DestructiveConfirmDialog
                open={confirmDialog === "confirm-dryrun"}
                onOpenChange={(o) => !o && setConfirmDialog(null)}
                title="Confirm (dry-run)"
                description="Locks the job into STRICT_RESTORE with dry_run=true. No DB mutation will occur on execute. Token + phrase required by backend guard."
                confirmLabel="Confirm dry-run"
                confirming={loading === "confirm"}
                onConfirm={() => runConfirm(true)}
                testId="dialog-confirm-dryrun"
            />
            <DestructiveConfirmDialog
                open={confirmDialog === "confirm-real"}
                onOpenChange={(o) => !o && setConfirmDialog(null)}
                title="Confirm (REAL — next step will be destructive)"
                description="Locks the job into STRICT_RESTORE with dry_run=false. The subsequent Execute step will wipe and restore operational collections. A checkpoint is created first."
                confirmLabel="Confirm real cycle"
                confirming={loading === "confirm"}
                onConfirm={() => runConfirm(false)}
                testId="dialog-confirm-real"
            />
            <DestructiveConfirmDialog
                open={confirmDialog === "execute"}
                onOpenChange={(o) => !o && setConfirmDialog(null)}
                title={job?.dry_run ? "Execute dry-run plan" : "DESTRUCTIVE · Execute restore"}
                description={
                    job?.dry_run
                        ? "Returns the planned wipe/restore counts. No DB mutation."
                        : "Will create a checkpoint, then wipe and restore all operational collections (clients, invoices, AMC, etc.). Audit-logged. Reversible via Step 7 within 30 days."
                }
                confirmLabel={job?.dry_run ? "Execute dry-run" : "Execute restore now"}
                confirming={loading === "execute"}
                onConfirm={runExecute}
                testId="dialog-execute"
                extraInfo={
                    job && (
                        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs space-y-0.5">
                            <div><span className="text-slate-500">Job:</span> <span className="font-mono">{job.id}</span></div>
                            <div><span className="text-slate-500">Mode:</span> {job.mode || "—"}</div>
                            <div><span className="text-slate-500">dry_run:</span> {String(job.dry_run)}</div>
                            <div><span className="text-slate-500">Source:</span> {job.source}</div>
                            <div><span className="text-slate-500">Snapshot SHA-256:</span> <span className="font-mono">{(job.checksum || "").slice(0, 24)}…</span></div>
                        </div>
                    )
                }
            />
            <DestructiveConfirmDialog
                open={confirmDialog === "rollback"}
                onOpenChange={(o) => !o && setConfirmDialog(null)}
                title="DESTRUCTIVE · Rollback to checkpoint"
                description="Wipes all operational collections and restores them from this job's pre-execute checkpoint. Adds an append-only entry to forensics.rollback_history."
                confirmLabel="Rollback now"
                confirming={loading === "rollback"}
                onConfirm={runRollback}
                testId="dialog-rollback"
                extraInfo={
                    checkpointId && (
                        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs space-y-0.5">
                            <div><span className="text-slate-500">Checkpoint:</span> <span className="font-mono">{checkpointId}</span></div>
                        </div>
                    )
                }
            />
        </div>
    );
}


// ════════════════════════════════════════════════════════════════════════
// Presentational sub-components
// ════════════════════════════════════════════════════════════════════════

function HealthStatusPill({ health }) {
    /* Iter 47.6 — Server-side config-health indicator.
     * Renders a green/red/amber pill so the operator can see whether
     * IMPORT_RESTORE_TOKEN is set on the SERVER (boolean only — the
     * actual value is never disclosed). Eliminates the
     * "did I mistype the token, or is the server not configured?"
     * class of confusion before any destructive step is attempted.
     */
    if (health.loading) {
        return (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-500 flex items-center gap-2" data-testid="health-pill">
                <CircleDashed size={14} className="animate-spin" />
                Probing server configuration…
            </div>
        );
    }
    if (health.error) {
        return (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800 flex items-center gap-2" data-testid="health-pill" data-testid-status="error">
                <Warning size={14} weight="fill" />
                Could not reach health endpoint · {health.error}
            </div>
        );
    }
    const tokenOk = !!health.import_restore_token_configured;
    return (
        <div
            data-testid="health-pill"
            data-testid-status={tokenOk ? "ready" : "missing"}
            className={`rounded-md border px-3 py-1.5 text-xs flex items-center gap-2 ${
                tokenOk
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-rose-200 bg-rose-50 text-rose-800"
            }`}
        >
            {tokenOk ? (
                <>
                    <CheckCircle size={14} weight="fill" />
                    <span className="font-medium">Server token configured</span>
                    <span className="text-emerald-700/80">·</span>
                    <span>framework {health.framework_version}</span>
                    <span className="text-emerald-700/80">·</span>
                    <span>schema {health.schema_version}</span>
                    <span className="text-emerald-700/80">·</span>
                    <span>bundled snapshot {health.bundled_snapshot_present ? "present" : "missing"}</span>
                </>
            ) : (
                <>
                    <XCircle size={14} weight="fill" />
                    <span className="font-medium">Server token NOT configured</span>
                    <span className="text-rose-700/80">·</span>
                    <span>Set <span className="font-mono">IMPORT_RESTORE_TOKEN</span> in this environment&apos;s env-var configuration and redeploy before any destructive step.</span>
                </>
            )}
        </div>
    );
}

function StepCard({ icon: Icon, title, subtitle, children, testId, active, disabledReason }) {
    return (
        <section
            data-testid={testId}
            className={`rounded-lg border bg-white p-5 transition-colors ${active ? "border-slate-300 shadow-sm" : "border-slate-200"}`}
        >
            <header className="flex items-start gap-3 mb-3">
                <div className={`shrink-0 w-9 h-9 rounded-md flex items-center justify-center ${active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
                    <Icon size={18} weight={active ? "fill" : "regular"} />
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <h2 className="font-display text-base font-semibold text-slate-900">{title}</h2>
                        {disabledReason && (
                            <Badge variant="outline" className="text-[10px] text-slate-500">
                                {disabledReason}
                            </Badge>
                        )}
                    </div>
                    {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
                </div>
            </header>
            <div className="space-y-3">{children}</div>
        </section>
    );
}

function JobSummary({ job, onRefresh, onCopyId }) {
    const detection = (job.structural_report?.info || []).find(
        (s) => typeof s === "string" && s.startsWith("detected_shape:")
    );
    return (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs space-y-1.5" data-testid="job-summary">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-slate-900">{job.id}</span>
                <button onClick={onCopyId} className="text-slate-400 hover:text-slate-900"><Copy size={11} /></button>
                <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${phaseBadgeStyle(job.phase)}`} data-testid="job-phase">
                    {job.phase}
                </span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-500">source: {job.source}</span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-500">mode: {job.mode || "—"}</span>
                {detection && (
                    <Badge variant="outline" className="text-[10px] font-mono" data-testid="job-detected-shape">
                        {detection.replace("detected_shape:", "shape · ")}
                    </Badge>
                )}
                <button onClick={onRefresh} className="text-slate-400 hover:text-slate-900 ml-auto" title="Refresh">
                    <ArrowsClockwise size={12} />
                </button>
            </div>
            <div className="flex items-center gap-2 font-mono text-slate-500">
                <span>SHA-256:</span><span className="text-slate-900">{(job.checksum || "").slice(0, 32)}…</span>
            </div>
            {job.preview && (
                <div className="text-slate-500">
                    Incoming: <span className="text-slate-900">{job.preview.total_documents}</span> documents · schema {job.preview.schema_version || "1.0.0"}
                </div>
            )}
        </div>
    );
}

function ValidationReport({ report }) {
    const errs = report?.structural?.errors || [];
    const warns = report?.structural?.warnings || [];
    const cur = report?.current_db_counts || {};
    const inc = report?.incoming_counts || {};
    const collections = Array.from(new Set([...Object.keys(cur), ...Object.keys(inc)])).sort();
    return (
        <div className="space-y-2 mt-2" data-testid="validation-report">
            {errs.length === 0 ? (
                <div className="flex items-center gap-2 text-emerald-700 text-xs">
                    <CheckCircle size={14} weight="fill" />
                    Structural validation passed
                </div>
            ) : (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-2.5 text-xs space-y-1">
                    <div className="font-medium text-rose-700">Errors ({errs.length})</div>
                    {errs.map((e, i) => <div key={i} className="font-mono text-rose-900">• {e}</div>)}
                </div>
            )}
            {warns.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs space-y-1">
                    <div className="font-medium text-amber-700">Warnings ({warns.length})</div>
                    {warns.slice(0, 5).map((w, i) => <div key={i} className="text-amber-900">• {w}</div>)}
                </div>
            )}
            {collections.length > 0 && (
                <div className="rounded-md border border-slate-200 bg-white">
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                <th className="text-left px-3 py-1.5 font-medium">Collection</th>
                                <th className="text-right px-3 py-1.5 font-medium">Live DB</th>
                                <th className="text-right px-3 py-1.5 font-medium">Incoming</th>
                                <th className="text-right px-3 py-1.5 font-medium">Δ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {collections.map((c) => {
                                const a = cur[c] ?? 0; const b = inc[c] ?? 0;
                                const d = b - a;
                                return (
                                    <tr key={c} className="border-t border-slate-100">
                                        <td className="px-3 py-1 font-mono">{c}</td>
                                        <td className="px-3 py-1 text-right">{a}</td>
                                        <td className="px-3 py-1 text-right">{b}</td>
                                        <td className={`px-3 py-1 text-right font-medium ${d === 0 ? "text-slate-400" : d > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                                            {d > 0 ? `+${d}` : d}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function PreviewPanel({ preview }) {
    const collections = Object.keys(preview.sample_records || {});
    return (
        <div className="space-y-2 mt-2" data-testid="preview-panel">
            <div className="text-xs text-slate-500">
                Sample records (top 3 per collection) · {collections.length} collections.
            </div>
            <details className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs">
                <summary className="cursor-pointer text-slate-600">Import order ({preview.import_order?.length || 0})</summary>
                <div className="mt-2 font-mono text-slate-700">
                    {(preview.import_order || []).join(" → ")}
                </div>
            </details>
            <details className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs">
                <summary className="cursor-pointer text-slate-600">Protected collections (never imported)</summary>
                <div className="mt-2 font-mono text-slate-700">
                    {(preview.protected_collections || []).join(", ")}
                </div>
            </details>
        </div>
    );
}

function DryRunPanel({ plan }) {
    return (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs mt-2" data-testid="dry-run-panel">
            <div className="font-medium text-blue-900 mb-1.5">Dry-run plan · no DB mutation</div>
            <div className="text-blue-900">
                Would restore <span className="font-mono">
                    {Object.values(plan?.would_restore || {}).reduce((a, b) => a + b, 0)}
                </span> documents across <span className="font-mono">{Object.keys(plan?.would_restore || {}).length}</span> collections.
            </div>
        </div>
    );
}

function ExecuteResultPanel({ result }) {
    const log = result.execution_log || {};
    const total = log.total_documents_restored;
    return (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs mt-2 space-y-1.5" data-testid="execute-result-panel">
            <div className="font-medium text-emerald-900">
                Restore complete · {total} documents restored
            </div>
            <div className="text-emerald-900 font-mono text-[11px]">
                Checkpoint: {result.checkpoint?.id} · SHA-256 {(result.checkpoint?.checksum || "").slice(0, 16)}…
            </div>
        </div>
    );
}

function RollbackPanel({ result }) {
    return (
        <div className="rounded-md border border-fuchsia-200 bg-fuchsia-50 p-3 text-xs mt-2" data-testid="rollback-panel">
            <div className="font-medium text-fuchsia-900">
                Rollback complete · {result.total_documents_restored} documents restored from checkpoint {result.checkpoint_id}
            </div>
        </div>
    );
}

function RollbackHistory({ history }) {
    return (
        <details className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs mt-2" data-testid="rollback-history">
            <summary className="cursor-pointer text-slate-600">
                Rollback history (append-only) · {history.length}
            </summary>
            <div className="mt-2 space-y-1 font-mono text-slate-700">
                {history.map((h, i) => (
                    <div key={i}>
                        #{i + 1} · {h.rolled_back_at} · by {h.rolled_back_by} · {h.total_documents_restored} docs
                    </div>
                ))}
            </div>
        </details>
    );
}

function ForensicBlock({ forensics, onCopy }) {
    if (!forensics) return null;
    return (
        <div className="rounded-md border border-slate-200 bg-white p-3 text-xs mt-2 space-y-1" data-testid="forensic-block">
            <div className="font-medium text-slate-900 mb-1">Forensic block (immutable)</div>
            {[
                ["snapshot_checksum",       forensics.snapshot_checksum],
                ["snapshot_schema_version", forensics.snapshot_schema_version],
                ["snapshot_app_version",    forensics.snapshot_app_version],
                ["checkpoint_id",           forensics.checkpoint_id],
                ["checkpoint_checksum",     forensics.checkpoint_checksum],
                ["checkpoint_size_bytes",   forensics.checkpoint_size_bytes],
                ["app_version",             forensics.app_version],
                ["schema_version",          forensics.schema_version],
                ["mode",                    forensics.mode],
                ["executed_by",             forensics.executed_by],
                ["executed_at",             forensics.executed_at],
            ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-[180px_1fr_auto] gap-2 items-center">
                    <span className="text-slate-500 font-mono">{k}</span>
                    <span className="text-slate-900 font-mono truncate">{String(v ?? "—")}</span>
                    {v && (
                        <button onClick={() => onCopy(v, k)} className="text-slate-400 hover:text-slate-900">
                            <Copy size={11} />
                        </button>
                    )}
                </div>
            ))}
            {forensics.execution_summary && (
                <div className="pt-1 mt-1 border-t border-slate-100 text-slate-500 font-mono text-[11px]">
                    summary: {JSON.stringify(forensics.execution_summary)}
                </div>
            )}
        </div>
    );
}

function RunTracker({ run, onAreaChange, onNotesChange }) {
    const summary = useMemo(() => {
        const counts = { pass: 0, fail: 0, pending: 0 };
        Object.values(run.areas).forEach((a) => { counts[a.status] = (counts[a.status] || 0) + 1; });
        return counts;
    }, [run]);

    return (
        <>
            <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="run-tracker-card">
                <header className="flex items-center gap-2 mb-3">
                    <ListChecks size={18} />
                    <h2 className="font-display text-base font-semibold text-slate-900">Verification tracker</h2>
                </header>
                <div className="text-xs text-slate-500 mb-3">
                    Started <span className="font-mono">{run.started_at?.slice(0, 19)}Z</span> · {summary.pass} pass / {summary.fail} fail / {summary.pending} pending
                </div>
                <div className="space-y-1.5">
                    {VERIFICATION_AREAS.map((a) => {
                        const cur = run.areas[a.key] || { status: "pending", note: "" };
                        return (
                            <div key={a.key} className="flex items-center gap-2" data-testid={`area-${a.key}`}>
                                <button
                                    onClick={() => onAreaChange(a.key, "pass")}
                                    className={`text-emerald-600 ${cur.status === "pass" ? "opacity-100" : "opacity-30 hover:opacity-100"}`}
                                    title="Mark pass"
                                    data-testid={`area-${a.key}-pass`}
                                >
                                    <CheckCircle size={16} weight={cur.status === "pass" ? "fill" : "regular"} />
                                </button>
                                <button
                                    onClick={() => onAreaChange(a.key, "fail")}
                                    className={`text-rose-600 ${cur.status === "fail" ? "opacity-100" : "opacity-30 hover:opacity-100"}`}
                                    title="Mark fail"
                                    data-testid={`area-${a.key}-fail`}
                                >
                                    <XCircle size={16} weight={cur.status === "fail" ? "fill" : "regular"} />
                                </button>
                                <button
                                    onClick={() => onAreaChange(a.key, "pending")}
                                    className={`text-slate-400 ${cur.status === "pending" ? "opacity-100" : "opacity-30 hover:opacity-100"}`}
                                    title="Reset to pending"
                                    data-testid={`area-${a.key}-pending`}
                                >
                                    <CircleDashed size={16} />
                                </button>
                                <span className={`text-xs ${cur.status === "pass" ? "text-emerald-700" : cur.status === "fail" ? "text-rose-700" : "text-slate-600"}`}>
                                    {a.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </section>
            <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="operator-notes-card">
                <header className="flex items-center gap-2 mb-3">
                    <Stack size={18} />
                    <h2 className="font-display text-base font-semibold text-slate-900">Operator notes</h2>
                </header>
                <Textarea
                    rows={6}
                    value={run.operator_notes}
                    onChange={(e) => onNotesChange(e.target.value)}
                    placeholder="Capture observations, anomalies, environment context, sign-off rationale…"
                    data-testid="operator-notes-input"
                />
                <p className="text-[10px] text-slate-400 mt-2">
                    Auto-saved to browser. Export via the "Export run record" action in the header to persist off-platform.
                </p>
            </section>
        </>
    );
}
